import {
  type DealerConfig,
  DEFAULT_PAGE_SIZE,
  type FilterState,
  type Location,
  type LocationDTO,
  type PaginatedResponse,
} from "@lyteflo/types";
import {
  authFetch,
  basicAuthRequest,
  buildListUrlParams,
  isValidPostalCode,
} from "@lyteflo/utils";
import {
  type CreateWifi,
  type DealerGroup,
  type DealerGroupDTO,
  type Dealership,
  type DealershipDTO,
  type UpdateWifi,
  type Wifi,
  type WifiDTO,
} from "@portalTypes/configuration";
import { getBasicAuthCredentials } from "@utils/environment";

const CONFIGURATION_API_URL = `${import.meta.env.VITE_API_BASE_URL}${import.meta.env.VITE_API_CONFIGURATION}`;
const BASIC_AUTH = getBasicAuthCredentials();

export const getDealerConfiguration = async (
  dealerId: string,
): Promise<DealerConfig> => {
  try {
    const { data: dealerConfiguration } = await basicAuthRequest<DealerConfig>(
      `${CONFIGURATION_API_URL}/api/v1/config/${encodeURIComponent(dealerId)}`,
      BASIC_AUTH,
    );

    return dealerConfiguration;
  } catch (error) {
    console.error("Error fetching dealer configuration: ", error);
    throw error;
  }
};

export const mapDealerGroupDTOtoDealerGroup = (
  group: DealerGroupDTO,
): DealerGroup => {
  return {
    id: group.id,
    name: group.name,
    dealers: group.dealers?.map((dealer) => ({
      configurationId: dealer.configuration_id,
      inventoryProvider: dealer.inventory_provider,
      dealerId: dealer.dealer_id,
      name: dealer.name,
    })),
  };
};

export const mapDealershipDTOToDealership = (
  dealer: DealershipDTO,
): Dealership => {
  return {
    configurationId: dealer.configuration_id,
    inventoryProvider: dealer.inventory_provider,
    dealerId: dealer.dealer_id,
    name: dealer.name,
  };
};

export const getWifis = async (
  requestPage: number = 1,
  requestLimit: number = DEFAULT_PAGE_SIZE,
  filterState?: FilterState,
): Promise<PaginatedResponse<Wifi>> => {
  try {
    const urlParams = buildListUrlParams(
      requestPage,
      requestLimit,
      filterState,
    );

    const { data, page, limit, total, totalPages, status } = await authFetch<
      WifiDTO[]
    >(`${CONFIGURATION_API_URL}/api/v1/wifi?${urlParams.toString()}`, {}, true);

    if (status === 404) {
      return {
        data: [],
        page: requestPage,
        limit: requestLimit,
        total: 0,
        totalPages: 0,
      };
    }

    return {
      data: (data ?? []).map(mapWifiDTOtoWifi),
      page: page ?? 1,
      limit: limit ?? DEFAULT_PAGE_SIZE,
      total: total ?? 0,
      totalPages: totalPages ?? 1,
    };
  } catch (error) {
    console.error("Error fetching devices", error);
    throw error;
  }
};

export const createWifi = async (wifi: CreateWifi) => {
  try {
    const { data: newWifi } = await authFetch<WifiDTO>(
      `${CONFIGURATION_API_URL}/api/v1/wifi`,
      {
        method: "POST",
        body: {
          ssid: wifi.ssid,
          password: wifi.password,
          configuration: {
            id: wifi.configurationId,
          },
        },
      },
    );
    return newWifi;
  } catch (error) {
    console.error("Error creating wifi: ", error);
    return null;
  }
};

export const updateWifi = async (wifi: UpdateWifi) => {
  try {
    const { data: updatedWifi } = await authFetch<WifiDTO>(
      `${CONFIGURATION_API_URL}/api/v1/wifi/${wifi.id}`,
      {
        method: "PATCH",
        body: {
          configuration: {
            id: wifi.configurationId,
          },
          password: wifi.password,
        },
      },
    );
    return updatedWifi;
  } catch (error) {
    console.error("Error updating wifi: ", error);
    return null;
  }
};

export const deleteWifi = async (id: string) => {
  try {
    const { data: deletedWifi } = await authFetch<WifiDTO>(
      `${CONFIGURATION_API_URL}/api/v1/wifi/${id}`,
      {
        method: "DELETE",
      },
    );
    return deletedWifi;
  } catch (error) {
    console.error("Error deleting wifi: ", error);
    return null;
  }
};

export const mapWifiDTOtoWifi = (wifi: WifiDTO): Wifi => {
  return {
    id: wifi.id,
    configuration: {
      id: wifi.configuration.id,
      name: wifi.configuration.name,
    },
    ssid: wifi.ssid,
    password: wifi.password,
  };
};

export const getLocation = async (zipCode: string) => {
  if (!isValidPostalCode(zipCode)) {
    throw Error("Invalid zip code");
  }

  try {
    const { data: location, status } = await basicAuthRequest<LocationDTO>(
      `${CONFIGURATION_API_URL}/api/v1/address/${zipCode}`,
      BASIC_AUTH,
    );
    if (status === "error") {
      throw Error("Zip code not found");
    }
    return mapLocationDTOtoLocation(location);
  } catch (error) {
    console.error("Failed to get location", error);
    throw error;
  }
};

export const mapLocationDTOtoLocation = (location: LocationDTO): Location => {
  return {
    locality: location.locality,
    regionCode: location.region_code,
    regionName: location.region_name,
    stateCode: location.state_code,
    stateName: location.state_name,
    zipCode: location.zip_code,
    countryCode: location.country_code,
  };
};
